import { useTranslation } from "react-i18next"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

import { parseFairUseLeadAllowance } from "./fair-use-settings-utils"

type FairUseLeadAllowanceFieldProps = {
  /** The raw form value, as typed — parsing is the field's own business. */
  value: string
  onChange: (value: string) => void
  className?: string
}

/**
 * How much budget one local AI lead may hand out each month.
 *
 * It is a field on the fair-use config document, but it answers a question the
 * lead roster asks, so round 4 shows it next to the roster on Fair use →
 * Users & licences rather than among the thresholds. The markup lives here so
 * both surfaces render the same control with the same id and validation, and
 * so moving it again is one import.
 */
export function FairUseLeadAllowanceField({
  value,
  onChange,
  className,
}: FairUseLeadAllowanceFieldProps) {
  const { t } = useTranslation()
  const hasIssue = parseFairUseLeadAllowance(value) == null

  return (
    <div className={cn("space-y-2", className)} data-testid="fair-use-lead-allowance">
      <Label className="text-sm font-semibold" htmlFor="fair-use-lead-allowance">
        {t("adminDashboard.fairUse.settings.leadAllowance.label", "A lead may approve")}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          className="h-10 w-28 tabular-nums"
          id="fair-use-lead-allowance"
          inputMode="decimal"
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <span className="text-muted-foreground text-xs">
          {t("adminDashboard.fairUse.settings.leadAllowance.unit", "SEK / month, per lead")}
        </span>
      </div>
      <p className="text-muted-foreground max-w-prose text-xs">
        {t(
          "adminDashboard.fairUse.settings.leadAllowance.help",
          "How much budget one local AI lead may hand out each month, across everyone assigned to them. 0 means leads approve nothing and every request comes to you."
        )}
      </p>
      {hasIssue ? (
        <p className="text-destructive text-xs" data-testid="fair-use-lead-allowance-error">
          {t(
            "adminDashboard.fairUse.settings.leadAllowance.rangeError",
            "The allowance is 0 SEK or more. Write decimals with a comma or a point (1,50) and no thousands grouping."
          )}
        </p>
      ) : null}
    </div>
  )
}
